export type Palette = {
  name: string;
  bg: string;
  primary: string;
  accent: string;
  ink: string;
  soft: string;
  // Optional override for the listPin bottom bar. Defaults to `ink` when
  // unset. Used so dark palettes (where `ink` is cream) can opt out of a
  // warm-cream bar in favor of a cooler/neutral grey.
  barBg?: string;
  // Optional override for the bar's right-side CTA text. Defaults to `accent`
  // when unset. Lets dark palettes pick a CTA color with enough contrast
  // against `barBg` (where `accent` may read muddy).
  barCta?: string;
  // Optional override for the spot-number text rendered inside the round
  // numbered chip on listPin rows (and the inline number on carousel stops).
  // Defaults to `bg` on listPin and `ink` on carousel when unset. Used so
  // palettes with mid-tone primary colors get a clean white "1/2/3" instead
  // of inheriting a muted text-on-circle.
  numText?: string;
  // Optional override for the italic accent word inside the headline.
  // Default behavior: accent inherits the headline body color (`ink`) — the
  // italic alone differentiates it. Set this when a palette wants a colored
  // accent that pops away from `ink` (e.g. Moody Dark uses its coral
  // primary to keep the headline animated against the dark navy bg).
  accentText?: string;
  // Optional override for the circle background behind the spot number on
  // carousel stops. Defaults to `accent` when unset. Used by Jade so the
  // numbered circles read as dark green (matches the bottom bar) instead of
  // the cream accent that gets lost against the sage background.
  carouselStopNumBg?: string;
};

export const palettes = {
  sageCream: {
    name: "Sage & Cream",
    bg: "#F5F1E8",
    primary: "#7A8471",
    accent: "#D4A574",
    ink: "#2C3E2D",
    soft: "#E8DDD0",
    numText: "#FFFFFF",
  },
  warmTerracotta: {
    name: "Warm Terracotta",
    bg: "#FDF6E3",
    primary: "#C65D3D",
    accent: "#E8B04B",
    ink: "#3D2817",
    soft: "#F4DCC0",
  },
  softPastel: {
    name: "Soft Pastel",
    bg: "#FFF5F7",
    primary: "#E8A0B8",
    accent: "#A8C5D6",
    ink: "#4A3849",
    soft: "#FDE8EC",
    numText: "#FFFFFF",
  },
  moodyDark: {
    name: "Moody Dark",
    // Navy + coral + slate, mapped from the provided reference palette.
    // Slate carries the listPin bar (lightest color = highest contrast
    // against the navy bg); coral leads as `primary` for headline pops.
    bg: "#23314A",
    primary: "#DB8084",
    accent: "#8FA1B1",
    ink: "#E8EEF3",
    soft: "#2D3D5A",
    barBg: "#8FA1B1",
    barCta: "#23314A",
    numText: "#FFFFFF",
    // Moody Dark is the only palette that keeps a colored accent — coral
    // primary against navy bg + cream ink keeps headlines animated.
    accentText: "#DB8084",
  },

  // ── Pinterest Palette 2026 ─────────────────────────────────────────
  // Five trending hues forecasted by Pinterest's 2026 color report.
  // Each palette features the trending color as `bg` with supporting
  // roles selected for WCAG-compliant contrast on pin imagery.

  coolBlue: {
    name: "Cool Blue",
    // Pinterest's "frosty, glacier aesthetic" — icy pale blue background.
    // Dark navy ink for high contrast; mid-tone blue leads as the headline
    // accent so it pops without breaking the cool-temperature mood.
    bg: "#D7EFFF",
    primary: "#2E5A8C",
    accent: "#74A8D4",
    ink: "#14253A",
    soft: "#EAF5FF",
    numText: "#FFFFFF",
  },

  jade: {
    name: "Jade",
    // "Between mint and moss" — earthy sage-green background. Deeper
    // moss for primary keeps the temperature; warm cream accent breaks
    // up the green so titles + badges don't blur into the bg.
    bg: "#AEB8A0",
    primary: "#4F6B4D",
    accent: "#DDD4C0",
    ink: "#1F2E1E",
    soft: "#C5CCB8",
    barBg: "#4F6B4D",
    barCta: "#DDD4C0",
    numText: "#FFFFFF",
    // Cream circles on a sage bg get muddy at small sizes — promote the
    // carousel spot-number circles to the deeper moss tone so they read
    // as a clear marker and pick up the same color as the bottom bar.
    carouselStopNumBg: "#4F6B4D",
  },

  plumNoir: {
    name: "Plum Noir",
    // "Burnt burgundy + chocolate brown" — very dark plum background.
    // Mauve rose pops as the primary (warm tonal lift on a cool dark
    // base); ink is a warm off-white for readability.
    bg: "#351E28",
    primary: "#B86E89",
    accent: "#6D3D52",
    ink: "#F2E8E0",
    soft: "#4A2D38",
    barBg: "#F2E8E0",
    barCta: "#351E28",
    numText: "#FFFFFF",
  },

  wasabi: {
    name: "Wasabi",
    // Electric chartreuse background — Pinterest's "high-voltage" hue.
    // Near-black olive ink keeps the punch readable; deep olive
    // primary gives titles weight without dimming the bg energy.
    bg: "#E9F056",
    primary: "#1F2407",
    accent: "#5C7029",
    ink: "#1A1F0A",
    soft: "#F4F89A",
    barBg: "#1F2407",
    barCta: "#E9F056",
  },
} as const satisfies Record<string, Palette>;

export type PaletteName = keyof typeof palettes;

export const defaultPalette = palettes.warmTerracotta;
